import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  X,
  Send,
  ArrowLeft,
  DollarSign,
  CheckCircle2,
  Target,
  Plus,
  Trash2,
  Check,
  Mail,
  MessageSquare,
  Link2,
  Loader2,
  Radio,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * MILESTONE OBJECTIVES BUILDER
 * Contractor Overwatch Design System - Spec-Ops Theme
 * Dark Mode + Emerald for money + Amber for actions
 */
const MilestoneSOWBuilder = ({
  estimateId,
  estimateData,
  clientInfo,
  companySettings,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [publicLink, setPublicLink] = useState(null);
  
  const [projectName, setProjectName] = useState(clientInfo?.project_name || "Project");
  const [projectAddress, setProjectAddress] = useState(clientInfo?.address || "");
  const [projectTimeline, setProjectTimeline] = useState("4-6 weeks");
  const [startDate, setStartDate] = useState("");
  
  const [milestones, setMilestones] = useState([
    { id: 1, name: "Mobilization & Setup", description: "Site preparation, permits, material staging", percentage: 10, deliverables: ["Site cleared and prepped", "Materials on-site", "Permits posted"], estimatedDuration: "2-3 days" },
    { id: 2, name: "Phase 1 - Foundation", description: "Primary structural work and foundation", percentage: 30, deliverables: ["Foundation complete", "Structural inspection passed"], estimatedDuration: "1-2 weeks" },
    { id: 3, name: "Phase 2 - Construction", description: "Main construction and build-out", percentage: 35, deliverables: ["Framing complete", "MEP rough-in", "Insulation installed"], estimatedDuration: "2-3 weeks" },
    { id: 4, name: "Phase 3 - Finishing", description: "Finish work, fixtures, and final details", percentage: 15, deliverables: ["Finish work complete", "Fixtures installed", "Paint/trim done"], estimatedDuration: "1-2 weeks" },
    { id: 5, name: "Final Closeout", description: "Inspection, cleanup, and project handoff", percentage: 10, deliverables: ["Final inspection passed", "Punchlist complete", "Client walkthrough"], estimatedDuration: "2-3 days" }
  ]);

  const groups = estimateData?.groups || [];

  const calculateTotals = useCallback(() => {
    let total = 0;
    groups.forEach(group => {
      (group.items || []).forEach(item => {
        const qty = parseFloat(item.quantity) || 0;
        const clientPrice = parseFloat(item.clientPrice) || parseFloat(item.unitCost) || 0;
        total += qty * clientPrice;
      });
    });
    return total;
  }, [groups]);

  const totalProjectCost = calculateTotals();
  const totalPercentage = milestones.reduce((sum, m) => sum + m.percentage, 0);

  useEffect(() => { setLoading(false); }, [estimateId]);

  // ═══════════════════════════════════════════════════════════════
  // FETCH CHATGPT MILESTONE DATA
  // Only updates durations and percentages - keeps structure static
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    const fetchChatGPTMilestones = async () => {
      if (!estimateId) return;
      
      try {
        const response = await axios.get(`${API_BASE}/api/estimates/${estimateId}`, {
          withCredentials: true
        });
        const missionBrief = response.data?.mission_brief_documents;
        
        if (missionBrief?.milestone_objectives?.draws) {
          const draws = missionBrief.milestone_objectives.draws;
          
          // Map ChatGPT draws to milestones
          const chatgptMilestones = draws.map((draw, idx) => ({
            id: idx + 1,
            name: draw.milestone_name || `Milestone ${idx + 1}`,
            description: draw.completion_criteria?.join(", ") || "Per contract specifications",
            percentage: draw.percentage || 0,
            deliverables: draw.completion_criteria || ["Deliverable 1"],
            estimatedDuration: draw.target_date || "TBD"
          }));
          
          setMilestones(chatgptMilestones);
          console.log("[MilestoneSOWBuilder] Updated from ChatGPT:", chatgptMilestones);
        }
        
        // Update overall project timeline
        if (missionBrief?.client_proposal?.timeline) {
          const timeline = missionBrief.client_proposal.timeline;
          setProjectTimeline(timeline.recommended_duration || `${timeline.calendar_weeks?.typical || 12} weeks`);
        }
        
      } catch (error) {
        console.log("[MilestoneSOWBuilder] No ChatGPT data, using defaults");
      }
    };
    
    fetchChatGPTMilestones();
  }, [estimateId, API_BASE]);

  const handleEditMilestone = (idx, field, value) => {
    setMilestones(prev => prev.map((m, i) => i === idx ? { ...m, [field]: field === 'percentage' ? parseFloat(value) || 0 : value } : m));
  };

  const handleAddMilestone = () => {
    setMilestones(prev => [...prev, { id: Date.now(), name: "New Milestone", description: "Description of this milestone", percentage: 0, deliverables: ["Deliverable 1"], estimatedDuration: "TBD" }]);
  };

  const handleDeleteMilestone = (idx) => { setMilestones(prev => prev.filter((_, i) => i !== idx)); };

  const handleAddDeliverable = (milestoneIdx) => {
    setMilestones(prev => prev.map((m, i) => i === milestoneIdx ? { ...m, deliverables: [...m.deliverables, "New deliverable"] } : m));
  };

  const handleEditDeliverable = (milestoneIdx, deliverableIdx, value) => {
    setMilestones(prev => prev.map((m, i) => i === milestoneIdx ? { ...m, deliverables: m.deliverables.map((d, di) => di === deliverableIdx ? value : d) } : m));
  };

  const handleDeleteDeliverable = (milestoneIdx, deliverableIdx) => {
    setMilestones(prev => prev.map((m, i) => i === milestoneIdx ? { ...m, deliverables: m.deliverables.filter((_, di) => di !== deliverableIdx) } : m));
  };

  const handleCopyLink = async () => {
    if (!estimateId) return;
    setLinkLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/api/documents/share`, { document_type: 'milestone_sow', estimate_id: estimateId, data: { milestones, projectTimeline, startDate } }, { withCredentials: true });
      const link = response.data.public_url;
      setPublicLink(link);
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) { console.error('Error generating link:', err); }
    finally { setLinkLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-slate-400 text-lg">Loading milestone objectives...</div>
      </div>
    );
  }

  const hasEstimateData = estimateId || (groups && groups.length > 0);
  
  if (!hasEstimateData) {
    return (
      <div className="min-h-screen pt-14 bg-slate-950">
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between bg-slate-900 border-b border-slate-800 shadow-lg">
          <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-all font-medium border border-slate-700">
            <ArrowLeft className="h-4 w-4" />
            <span>← Exit</span>
          </button>
          <span className="text-emerald-400 font-bold uppercase tracking-wider">MILESTONE OBJECTIVES</span>
          <button onClick={onClose} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all border border-red-500/30">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center justify-center" style={{ minHeight: "calc(100vh - 60px)" }}>
          <div className="text-center p-8 rounded-lg bg-slate-900 border-2 border-dashed border-slate-700 max-w-md">
            <h2 className="text-xl font-bold text-white mb-4 uppercase tracking-wide">No Estimate Data</h2>
            <p className="text-slate-400 mb-6">Create an estimate first using the ESTIMATING COMMAND, then return here to build your Milestone Objectives.</p>
            <button onClick={onClose} className="px-6 py-3 rounded font-bold text-white transition-all hover:scale-105 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/20">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto pb-32 pt-14 bg-slate-950">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between bg-slate-900 border-b border-slate-800 shadow-lg">
        <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-all font-medium border border-slate-700">
          <ArrowLeft className="h-4 w-4" />
          <span>← Exit</span>
        </button>
        <div className="flex items-center gap-3">
          <span className="text-emerald-400 font-bold uppercase tracking-wider">MILESTONE OBJECTIVES</span>
          <span className="text-slate-600">|</span>
          <span className="text-slate-400">{projectName}</span>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all border border-red-500/30">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Document Container */}
      <div className="mx-auto mt-8 max-w-4xl bg-slate-900 rounded-lg border border-slate-800 shadow-lg overflow-hidden">
        
        {/* Document Header */}
        <div className="px-8 py-6 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center gap-3 mb-2">
            <Target className="h-5 w-5 text-emerald-400" />
            <p className="text-xs uppercase tracking-[4px] text-emerald-400 font-bold">MILESTONE OBJECTIVES</p>
          </div>
          <h1 className="text-3xl font-bold text-white uppercase tracking-wide">{projectName}</h1>
          <p className="text-slate-400 text-sm mt-1">{companySettings?.company_name || "Alpha Construction Pros"}</p>
          
          {/* Mission Details */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-xs text-slate-500 uppercase tracking-widest">MISSION</p>
              <input type="text" value={projectName} onChange={(e) => setProjectName(e.target.value)} className="mt-1 w-full bg-transparent text-white font-bold focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" />
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-xs text-slate-500 uppercase tracking-widest">LOCATION</p>
              <input type="text" value={projectAddress} onChange={(e) => setProjectAddress(e.target.value)} placeholder="Enter location" className="mt-1 w-full bg-transparent text-slate-300 focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500 placeholder-slate-600" />
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-xs text-slate-500 uppercase tracking-widest">EST. TIMELINE</p>
              <input type="text" value={projectTimeline} onChange={(e) => setProjectTimeline(e.target.value)} className="mt-1 w-full bg-transparent text-slate-300 focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" />
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-xs text-slate-500 uppercase tracking-widest">START DATE</p>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 w-full bg-transparent text-slate-300 focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" />
            </div>
          </div>
        </div>

        {/* Project Total */}
        <div className="px-8 py-6 border-b border-slate-800 bg-slate-800/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="h-6 w-6 text-emerald-400" />
              <span className="text-slate-400 uppercase tracking-wider text-sm">Total Mission Cost</span>
            </div>
            <span className="text-4xl font-mono font-bold text-emerald-400">${totalProjectCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </div>
          {totalPercentage !== 100 && (
            <div className="mt-3 px-4 py-2 rounded bg-red-500/10 border border-red-500/30 text-sm text-red-400">
              ⚠ ALERT: Milestone percentages total {totalPercentage}% (should be 100%)
            </div>
          )}
        </div>

        {/* Payment Objectives Section */}
        <div className="px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">PAYMENT OBJECTIVES</p>
            </div>
            <button onClick={handleAddMilestone} className="flex items-center gap-1 px-4 py-2 rounded font-bold text-white text-sm bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/20 transition-all">
              <Plus className="h-4 w-4" /> Add Objective
            </button>
          </div>

          <div className="space-y-4">
            {milestones.map((milestone, idx) => (
              <div key={milestone.id} className="p-5 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">{idx + 1}</div>
                    <div className="flex-1">
                      <input type="text" value={milestone.name} onChange={(e) => handleEditMilestone(idx, 'name', e.target.value)} className="w-full bg-transparent text-white font-bold focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" />
                      <input type="text" value={milestone.description} onChange={(e) => handleEditMilestone(idx, 'description', e.target.value)} className="w-full bg-transparent text-slate-400 text-sm mt-1 focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" />
                    </div>
                  </div>
                  <button onClick={() => handleDeleteMilestone(idx)} className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 border border-transparent hover:border-red-500/30">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="p-3 rounded bg-slate-900/50 border border-slate-700">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">PERCENTAGE</p>
                    <div className="flex items-center gap-2">
                      <input type="number" value={milestone.percentage} onChange={(e) => handleEditMilestone(idx, 'percentage', e.target.value)} className="w-16 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white font-mono text-center focus:outline-none focus:border-emerald-500" />
                      <span className="text-slate-400">%</span>
                    </div>
                  </div>
                  <div className="p-3 rounded bg-slate-900/50 border border-slate-700">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">AMOUNT</p>
                    <p className="text-emerald-400 font-mono font-bold text-lg">${((totalProjectCost * milestone.percentage) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="p-3 rounded bg-slate-900/50 border border-slate-700">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">EST. DURATION</p>
                    <input type="text" value={milestone.estimatedDuration} onChange={(e) => handleEditMilestone(idx, 'estimatedDuration', e.target.value)} className="w-full bg-transparent text-slate-300 focus:outline-none border-b border-slate-700 focus:border-emerald-500" />
                  </div>
                </div>

                {/* Deliverables */}
                <div className="p-3 rounded bg-slate-900/50 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">DELIVERABLES</p>
                    <button onClick={() => handleAddDeliverable(idx)} className="text-xs text-emerald-400 hover:text-emerald-300 font-medium">+ Add</button>
                  </div>
                  <div className="space-y-1">
                    {milestone.deliverables.map((deliverable, di) => (
                      <div key={di} className="flex items-center gap-2 py-1">
                        <CheckCircle2 className="h-3 w-3 text-emerald-400 flex-shrink-0" />
                        <input type="text" value={deliverable} onChange={(e) => handleEditDeliverable(idx, di, e.target.value)} className="flex-1 bg-transparent text-slate-300 text-sm focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" />
                        <button onClick={() => handleDeleteDeliverable(idx, di)} className="p-0.5 text-slate-600 hover:text-red-400"><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Footer */}
        <div className="px-8 py-6 bg-slate-800/50 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm">TOTAL OBJECTIVES: <span className="text-white font-mono">{milestones.length}</span></p>
              <p className="text-slate-500 text-sm">PAYMENT DISTRIBUTION: <span className={`font-mono ${totalPercentage === 100 ? 'text-emerald-400' : 'text-red-400'}`}>{totalPercentage}%</span></p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 uppercase tracking-wider">MISSION TOTAL</p>
              <p className="text-3xl font-mono font-bold text-emerald-400">${totalProjectCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-[200] px-6 py-4 bg-slate-900/95 border-t border-slate-800 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-all border border-slate-700">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">← Back</span>
          </button>
          <div className="flex items-center gap-3">
            <button onClick={handleCopyLink} disabled={linkLoading || !estimateId} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700">
              {linkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : linkCopied ? <Check className="h-4 w-4 text-emerald-400" /> : <Link2 className="h-4 w-4" />}
              {linkLoading ? "Preparing..." : linkCopied ? "Link Copied!" : "Copy Link"}
            </button>
            <button onClick={() => setSendModalOpen(true)} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold transition-all hover:scale-105 text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/20">
              <Send className="h-4 w-4" />
              Send Objectives
            </button>
          </div>
        </div>
      </div>

      {/* Send Modal */}
      {sendModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-[9999] bg-black/85 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) setSendModalOpen(false); }}>
          <div className="w-full max-w-md rounded-lg p-6 bg-slate-900 border border-slate-800 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white uppercase tracking-wide">Send Milestone Objectives</h3>
              <button onClick={() => setSendModalOpen(false)} className="p-1 rounded hover:bg-slate-800 text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <button onClick={() => setSendModalOpen(false)} className="w-full flex items-center gap-3 p-4 rounded-lg hover:bg-slate-800 transition-all border border-slate-700">
                <Mail className="h-5 w-5 text-slate-400" />
                <div className="text-left"><p className="font-medium text-white">Send via Email</p><p className="text-sm text-slate-500">{clientInfo?.email || "Enter email address"}</p></div>
              </button>
              <button onClick={() => setSendModalOpen(false)} className="w-full flex items-center gap-3 p-4 rounded-lg hover:bg-slate-800 transition-all border border-slate-700">
                <MessageSquare className="h-5 w-5 text-slate-400" />
                <div className="text-left"><p className="font-medium text-white">Send via SMS</p><p className="text-sm text-slate-500">{clientInfo?.phone || "Enter phone number"}</p></div>
              </button>
              <button onClick={handleCopyLink} disabled={linkLoading || !estimateId} className="w-full flex items-center gap-3 p-4 rounded-lg hover:bg-slate-800 transition-all border border-slate-700">
                {linkLoading ? <Loader2 className="h-5 w-5 text-slate-400 animate-spin" /> : linkCopied ? <Check className="h-5 w-5 text-emerald-400" /> : <Link2 className="h-5 w-5 text-slate-400" />}
                <div className="text-left"><p className="font-medium text-white">{linkCopied ? "Link Copied!" : "Copy Shareable Link"}</p><p className="text-sm text-slate-500">{!estimateId ? "An estimate must be selected" : "Share a direct link to this document"}</p></div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestoneSOWBuilder;
